import {
  type EvidenceTypeEnum,
  getApiTradeV1SiteMaterialsEvidenceListQueryKey,
  useApiTradeV1SiteMaterialsEvidenceListSuspense,
  useQueryClient,
} from 'api';
import { ActionBar, useManageEvidence, useSiteMaterial } from 'features';
import { useTranslation } from 'i18n';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid, Page, PhotoEvidenceMultiSelectList, Section, Typography, useBreakpoint } from 'ui';

import { SiteHeader } from '../../components/SiteHeader/SiteHeader';

export const EvidenceEdit = ({ evidenceType }: { evidenceType: EvidenceTypeEnum }) => {
  const { t } = useTranslation('orders');
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: siteMaterial } = useSiteMaterial();
  const { data } = useApiTradeV1SiteMaterialsEvidenceListSuspense(String(siteMaterial.id), {
    page_size: 10000,
    type: evidenceType,
  });
  const { selected, setSelected, deleteSelected, isPending } = useManageEvidence({
    mode: 'site-material',
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getApiTradeV1SiteMaterialsEvidenceListQueryKey(String(siteMaterial.id)),
      });
      navigate(-1);
    },
    parentId: siteMaterial.id,
  });

  if (!data) {
    return <Typography>{t('evidenceEditPage.materialNotFound')}</Typography>;
  }

  return (
    <Page>
      <Page.Title title={t('evidenceEditPage.pageTitle')} />
      <Page.Divider />
      <Grid container>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <Section title={t('evidenceEditPage.sections.siteDetails')}>
            <SiteHeader siteMaterial={siteMaterial} />
            <Box
              sx={{
                mt: 2,
                mb: 1,
              }}
            >
              <ActionBar
                selected={selected.length}
                handleClose={() => navigate(-1)}
                handleDelete={deleteSelected}
                actionPending={isPending}
              />
            </Box>
          </Section>
        </Grid>
        <Grid
          size={{
            md: 8,
            xs: 12,
          }}
        >
          <Section
            title={t('evidenceEditPage.sections.evidence')}
            action={
              <Button size="large" fullWidth variant="secondary" onClick={() => navigate(-1)} loading={isPending}>
                {t('evidenceEditPage.actions.cancel')}
              </Button>
            }
          >
            <PhotoEvidenceMultiSelectList
              slotProps={{
                imageList: {
                  cols: isMobile ? 3 : 5,
                },
              }}
              evidencePhotos={data.results.map(({ id, file, preview }) => ({
                id,
                src: (preview || file) ?? '',
              }))}
              onSelectionChange={setSelected}
            />
          </Section>
        </Grid>
      </Grid>
    </Page>
  );
};
